// EmotionsReport.js
import * as theme from '../../../../theme';
import logo from '../../../../images/tb-footer-gold.png';
import rhLogo from '../../../../images/rh-logo.png';
import maregyOtf from '../../../../theme/MAREGY.otf';

export function generateEmotionsReportHTML(grokData, brand = 'TruBalance') {
  const formatGrokAsHTML = (grokData) => {
    if (!grokData) return { main: '<p class="emotions-text">No summary available</p>', additional: '' };

    // Handle old string format (blob of text)
    if (typeof grokData === 'string') {
      const content = grokData.split('\n').map((line) => {
        if (line.trim().endsWith(':')) {
          return `<div class="emotion-heading">${line}</div>`;
        } else {
          return `<p class="emotions-text">${line}</p>`;
        }
      }).join('');
      return { main: content, additional: '' };
    }

    // Handle new object format
    if (typeof grokData === 'object') {
      let mainSections = '';
      let additionalSections = '';

      // Helper to add a section to main
      const addSection = (title, content) => {
        mainSections += `
          <div class="emotion-heading">${title}:</div>
          ${content}
        `;
      };

      // Primary Emotional Disharmony (always shown per assumptions, but hidden if explicitly "None")
      const primaryDisharmony = grokData.primaryDisharmony || 'None';
      if (primaryDisharmony !== 'None') {
        addSection(
          'Primary Emotional Disharmony',
          `<p class="emotions-text">${primaryDisharmony}</p>`
        );
      }

      // Factors Connected to Disharmony (always shown per assumptions, but hidden if explicitly "None")
      if (grokData.factorsConnected?.length > 0) {
        const factorsContent = `
          <div class="emotion-list">
            ${grokData.factorsConnected.map(factor => `<p class="emotions-text"> - ${factor}</p>`).join('')}
          </div>
        `;
        addSection('Factors Connected to Emotional Disharmony', factorsContent);
      }

      const bachFlowerBalance = grokData.bachFlowerBalance || [];
      if (bachFlowerBalance.length > 0) {
        const bachContent = bachFlowerBalance.map((flower, index) => {
          if (index > 0) {
            return `<p class="emotions-text flower-item"><span style="font-weight: 500; font-style: italic;">${flower.item}:</span> ${flower.description}</p>`;
          } else {
            return `<p class="emotions-text"><span style="font-weight: 500; font-style: italic;">${flower.item}:</span> ${flower.description}</p>`
          }
        }).join('');
        addSection('Bach Flower Balance', bachContent);
      }

      // Emotions Affecting (hidden if "None"/empty)
      if (grokData.emotionsAffecting?.length > 0 && grokData.emotionsAffecting[0] !== 'None' && grokData.emotionsAffecting[0] !== 'None.') {
        const affectingContent = `
          <div class="emotion-list">
            ${grokData.emotionsAffecting.map(emotion => `<p class="emotions-text">${emotion}</p>`).join('')}
          </div>
        `;
        addSection('Body Systems Affected by Emotions', affectingContent);
      }

      // Body Stressors (hidden if "None"/empty)
      if (grokData.bodyStressors?.length > 0) {
        const stressorsContent = `
          <div class="emotion-list">
            ${grokData.bodyStressors.map(stressor => `<p class="emotions-text">${stressor}</p>`).join('')}
          </div>
        `;
        addSection('Body Stressors', stressorsContent);
      }

      // Words of Affirmation (custom layout; only shown if words or description exist)
      const words = grokData.wordsOfAffirmation?.words || [];
      const description = grokData.wordsOfAffirmation?.description || '';
      if (words.length > 0 || description) {
        let wordsContent = '';
        if (words.length <= 2) {
          mainSections += `
            <div class="emotion-inline">
              <div class="emotion-heading">Words of Affirmation:</div>
              <span class="emotion-inline-text">${words.join(', ')}</span>
            </div>
          `;
          wordsContent = `<p class="emotions-text">${description}</p>`;
        } else {
          wordsContent = `<p class="emotions-text">${words.join(', ')} - ${description}</p>`;
        }
        if (words.length > 2) {
          addSection('Words of Affirmation', wordsContent);
        } else {
          mainSections += wordsContent;
        }
      }

      // Limiting Beliefs (hidden if "None"/empty)
      const limitingContent = grokData.limitingBeliefs?.length > 0
        ? grokData.limitingBeliefs.join(' ')
        : 'None';
      if (limitingContent !== 'None' && limitingContent !== 'None.') {
        addSection(
          'Limiting Beliefs',
          `<p class="emotions-text">${limitingContent}</p>`
        );
      }

      // Soul Filters (hidden if "None"/empty)
      const soulFilters = grokData.soulFilters || 'None';
      if (soulFilters !== 'None' && soulFilters !== 'None.') {
        addSection(
          'Soul Filters',
          `<p class="emotions-text">${soulFilters}</p>`
        );
      }

      // Additional Emotional Imbalances (already conditional; hidden if empty)
      if (grokData.emotionalImbalances?.length > 0) {
        additionalSections += `
          <div class="additional-header">
            ADDITIONAL EMOTIONAL IMBALANCES
          </div>
        `;

        const items = grokData.emotionalImbalances;
        const columns = [[], [], []];
        items.forEach((item, index) => {
          columns[index % 3].push(item);
        });

        additionalSections += `<div style="display: flex; justify-content: space-around; margin-top: 15px; padding-left: 15px; padding-right: 15px;">`;
        columns.forEach(col => {
          additionalSections += `<ul style="list-style-type: none; padding: 0; margin: 0; width: 30%;">`;
          col.forEach(item => {
            const capitalizedItem = item.charAt(0).toUpperCase() + item.slice(1);
            additionalSections += `<li>- ${capitalizedItem}</li>`;
          });
          additionalSections += `</ul>`;
        });
        additionalSections += `</div>`;
      }

      return { main: mainSections || '<p class="emotions-text">No summary available</p>', additional: additionalSections };
    }

    // Fallback
    return { main: '<p class="emotions-text">No summary available</p>', additional: '' };
  };

  const fontUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap';
  const importRule = `@import url('${fontUrl}');`;

  const fontFaceRule = `
    @font-face {
      font-family: 'Maregy';
      src: url('${maregyOtf}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;

  // Brand-specific toggles
  const logoSrc = brand === 'TruBalance' ? logo : rhLogo;
  const purpleColor = brand === 'TruBalance' ? theme.Colors.Purple : theme.Colors.RHPurple;
  const grayDark = brand === 'TruBalance' ? theme.Colors.GrayDark : theme.Colors.RHBlack;
  const logoMaxHeight = brand === 'TruBalance' ? '80px' : '50px';
  const logoMaxHeightPrint = brand === 'TruBalance' ? '63px' : '43px';

  const { main, additional } = formatGrokAsHTML(grokData);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Emotional Imbalances Report</title>
      <style>
        ${importRule}
        ${fontFaceRule}
        body { font-family: Montserrat, Arial, sans-serif; background: white; margin: 0; padding: 0; }
        .container { max-width: 800px; margin: 0 auto; padding: 10px; padding-left: 20px; padding-right: 20px; }
        .full-width { width: 100%; }
        .header-bar { background-color: ${grayDark}; height: 80px; width: 100%; display: flex; justify-content: center; align-items: center; }
        .logo { max-height: ${logoMaxHeight}; margin-top: 10px; margin-bottom: 10px; }
        .content { padding: 10px; padding-top: 0px; text-align: left; }
        .purple-bar { background-color: ${purpleColor}; height: 12px; width: 100%; margin-top: 10px; }
        .print-btn { position: absolute; top: 20px; right: 20px; padding: 8px 16px; background: ${purpleColor}; border-radius: 4px; color: white; border: none; cursor: pointer; }
        .print-btn:hover { background: ${theme.Colors.GoldDark}; }
        .main-content-wrapper { flex: 1; display: flex; flex-direction: column; }
        .additional { font-size: 14px; margin-top: auto; page-break-inside: avoid; }
        .additional-header { font-size: 14px; box-sizing: border-box; background-color: ${purpleColor}; height: 30px; width: 100%; display: flex; align-items: center; justify-content: flex-start; padding-left: 30px; color: white; font-weight: bold; text-transform: uppercase; margin-top: 20px; }
        .header-text { font-size: 26px; margin-top: 10px; font-family: Maregy; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1.2px; -webkit-text-stroke: 1px black; }
        .secondary-text { font-size: 13px; margin-bottom: 8px; margin-top: 0px; text-align: justify; line-height: 1.3 }
        .emotions-wrapper { position: relative; padding-bottom: 50px; }
        .emotions-container { padding-left: 20px; padding-right: 20px; box-sizing: border-box; }
        .emotion-heading { font-size: 16px; font-weight: bold; text-transform: uppercase; margin-top: 20px; margin-bottom: 4px; }
        .emotion-heading + .secondary-text { margin-top: 0; }
        .emotions-text { font-size: 14px; line-height: 1.3; margin-bottom: 0px; margin-top: 0px; }
        .emotion-list .secondary-text { margin-top: 10px; margin-bottom: 0; }
        .emotion-list .secondary-text:first-child { margin-top: 0; }
        .emotion-inline { display: flex; align-items: baseline; }
        .emotion-inline-text { font-size: 14px; font-weight: normal; text-transform: none; margin-left: 5px; }
        .additional-header { font-size: 14px; box-sizing: border-box; background-color: ${purpleColor}; height: 30px; width: 100%; display: flex; align-items: center; justify-content: flex-start; padding-left: 30px; color: white; font-weight: bold; text-transform: uppercase; margin-top: 20px; }
        .flower-item { margin-top: 7px; }
        @media print { 
          .print-btn { display: none; } 
          @page { margin: 0; size: letter portrait; }
          body { margin: 0; padding: 0; display: flex; flex-direction: column; height: 100%; }
          .container { max-width: 100%; height: auto; flex: 1; display: flex; flex-direction: column; box-sizing: border-box; }
          .header-bar { justify-content: flex-end; padding-right: 10px; flex-shrink: 0; padding-top: 10px; page-break-after: avoid; }
          .logo { max-height: ${logoMaxHeightPrint}; margin-top: 17px; margin-bottom: 10px; margin-right: 8px; }
          .purple-bar { page-break-inside: avoid; }
          .content { page-break-inside: avoid; }
          .additional { font-size: 12px; position: absolute; bottom: 20px; left: 20px; width: calc(100% - 40px); }
          .additional-header { font-size: 12px; }
          .header-text { font-size: 20px; margin-bottom: 6px; }
          .secondary-text { font-size: 13px; line-height: 1.3 }
          .emotion-heading { font-size: 12px; }
          .emotions-text { font-size: 12px; }
          .emotion-inline-text { font-size: 12px; }
          .emotions-wrapper { height: 1056px; padding-bottom: 0px; }
          .additional-header { font-size: 12px; }
          * { box-sizing: border-box; orphans: 1; widows: 1; }
        }
      </style>
    </head>
    <body>
      <div class="emotions-wrapper">
        <div class="header-bar">
          <img src="${logoSrc}" alt="Logo" class="logo">
        </div>
        <div class="container">
          <div class="full-width emotions-container">
            <p class="header-text">Emotional Imbalances</p>
            <p class="secondary-text">Emotions often underlie bodily imbalances. This scan summary highlights key emotional disturbances, where your body feels "stuck" in the states listed below. These may arise from physical traumas (retaining emotional imprints), generational inheritance (via cells), or absorbed from close relationships.</p>
            <p class="secondary-text">Bach flower essences are plant-based remedies that promote emotional harmony. You can purchase the recommended essence or let your imprint remedy carry its energetic signature for support.</p>
            <p class="secondary-text">By using your personalized imprint remedy, you encourage your body to work through these emotional blocks.</p>
          </div>
          <div class="purple-bar"></div>
          <div class="full-width emotions-container">
            <div class="content main-content-wrapper">
              ${main}
            </div>
          </div>
          <div class="full-width">
            <div class="additional">
              ${additional}
            </div>
          </div>
        </div>
      </div>
      <button class="print-btn" onclick="window.print()">Print</button>
    </body>
    </html>
  `;
}